/**
 * React hook for subscribing to backend WebSocket events.
 * Uses the unified WsClient for event subscriptions.
 * Handles notifications with user preferences and persistent alerts.
 */

import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWsEvents, type EngineEvent } from "./use-ws-subscription";
import { toast } from "sonner";
import { AlertTriangle, Skull, ShieldAlert, ShieldOff, CheckCircle2, TrendingUp, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import type { MarginWarningLevel, NotificationPreferences } from "../types";
import { usePersistentNotifications } from "@/components/persistent-notification";
import { getStoredToken } from "../auth";

import { API_BASE_URL } from "@/backend/config";

// Cooldown duration for margin warning toasts (4 hours in milliseconds)
const MARGIN_WARNING_COOLDOWN_MS = 4 * 60 * 60 * 1000;
const MARGIN_WARNING_STORAGE_KEY = "margin_warning_last_shown";

// Track recently shown order fill notifications to prevent duplicates
// Uses a Set with order_id keys, cleared after 30 seconds
const recentOrderFillNotifications = new Set<string>();
const ORDER_FILL_DEDUP_DURATION_MS = 30 * 1000; // 30 seconds

// Track recently shown deposit notifications to prevent duplicates
// Uses a Set with tx_signature keys, cleared after 60 seconds
const recentDepositNotifications = new Set<string>();
const DEPOSIT_DEDUP_DURATION_MS = 60 * 1000; // 60 seconds

// Check if we've already shown a notification for this order
const hasShownOrderFillNotification = (orderId: string): boolean => {
  return recentOrderFillNotifications.has(orderId);
};

// Mark an order as notified (auto-clears after dedup duration)
const markOrderFillNotified = (orderId: string): void => {
  recentOrderFillNotifications.add(orderId);
  setTimeout(() => {
    recentOrderFillNotifications.delete(orderId);
  }, ORDER_FILL_DEDUP_DURATION_MS);
};

// Check if we've already shown a notification for this deposit
const hasShownDepositNotification = (txSignature: string): boolean => {
  return recentDepositNotifications.has(txSignature);
};

// Mark a deposit as notified (auto-clears after dedup duration)
const markDepositNotified = (txSignature: string): void => {
  recentDepositNotifications.add(txSignature);
  setTimeout(() => {
    recentDepositNotifications.delete(txSignature);
  }, DEPOSIT_DEDUP_DURATION_MS);
};

// Get the last time a margin warning was shown for a specific market/level
const getLastWarningTime = (market: string, level: MarginWarningLevel): number => {
  try {
    const stored = localStorage.getItem(MARGIN_WARNING_STORAGE_KEY);
    if (!stored) return 0;
    const data = JSON.parse(stored);
    return data[`${market}_${level}`] || 0;
  } catch {
    return 0;
  }
};

// Set the last time a margin warning was shown for a specific market/level
const setLastWarningTime = (market: string, level: MarginWarningLevel): void => {
  try {
    const stored = localStorage.getItem(MARGIN_WARNING_STORAGE_KEY);
    const data = stored ? JSON.parse(stored) : {};
    data[`${market}_${level}`] = Date.now();
    localStorage.setItem(MARGIN_WARNING_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore localStorage errors
  }
};

// Check if enough time has passed to show a margin warning again
const canShowMarginWarning = (market: string, level: MarginWarningLevel): boolean => {
  const lastShown = getLastWarningTime(market, level);
  return Date.now() - lastShown >= MARGIN_WARNING_COOLDOWN_MS;
};

// Format currency for display
const formatUsd = (amount: string | number) => {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

// Get warning message based on level
const getWarningMessage = (level: MarginWarningLevel, market: string) => {
  switch (level) {
    case "danger":
      return {
        title: "High Risk Position",
        description: `Your ${market} position is at high risk. Add margin or reduce position size to avoid liquidation.`,
        icon: Skull,
        className: "text-ask-foreground",
      };
    case "warning":
      return {
        title: "Margin Warning",
        description: `Your ${market} position is approaching liquidation. Consider reducing leverage or adding margin.`,
        icon: ShieldAlert,
        className: "text-orange-500",
      };
    case "caution":
      return {
        title: "Low Margin",
        description: `Your ${market} position margin is below recommended levels.`,
        icon: AlertTriangle,
        className: "text-yellow-500",
      };
    default:
      return null;
  }
};

// Helper to create notification in backend
const createNotification = async (
  type: string,
  severity: string,
  title: string,
  message: string,
  metadata?: Record<string, any>
) => {
  try {
    const token = getStoredToken();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    await fetch(`${API_BASE_URL}/api/notifications`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify({ type, severity, title, message, metadata }),
    });
  } catch (error) {
    console.error("Failed to create notification:", error);
  }
};

// Helper to get notification preferences
const getPreferences = async (): Promise<NotificationPreferences | null> => {
  try {
    const token = getStoredToken();
    const headers: Record<string, string> = {};
    
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    const response = await fetch(`${API_BASE_URL}/api/notification-preferences`, {
      headers,
      credentials: "include",
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
};

export const useBackendEvents = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  const [lastEvent, setLastEvent] = useState<EngineEvent | null>(null);
  const { addNotification: addPersistentNotification } = usePersistentNotifications();

  const handleEvent = useCallback(
    async (event: EngineEvent) => {
      setLastEvent(event);
      
      // Get user preferences
      const prefs = await getPreferences();

      // Invalidate relevant queries based on event type
      switch (event.type) {
        case "order_placed":
        case "order_cancelled":
          queryClient.invalidateQueries({
            queryKey: ["backend-orders", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["backend-positions", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["backend-account", userId],
          });
          break;

        case "order_filled":
          // Only notify for LIMIT orders (market orders are synchronous - user knows)
          // Also check: it's for this user AND not already shown (deduplication)
          if (event.user_id === userId && 
              event.order_type === "limit" &&
              prefs?.order_filled_enabled && 
              !hasShownOrderFillNotification(event.order_id)) {
            // Mark as shown to prevent duplicates
            markOrderFillNotified(event.order_id);
            
            const title = "Order Filled";
            const message = `Your ${event.market} order was filled at ${formatUsd(event.fill_price)} (Size: ${event.fill_size})`;
            
            // Show toast IMMEDIATELY (don't wait for backend)
            if (prefs.order_filled_toast) {
              toast.success(title, {
                description: message,
                duration: 5000,
                icon: <CheckCircle2 className="text-bid-foreground" />,
              });
            }
            
            // Create notification in background (fire-and-forget, don't await)
            createNotification("order_filled", "success", title, message, {
              order_id: event.order_id,
              market: event.market,
              fill_price: event.fill_price,
              fill_size: event.fill_size,
            });
          }
          
          queryClient.invalidateQueries({
            queryKey: ["backend-orders", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["backend-positions", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["backend-account", userId],
          });
          // Also refresh history tabs when orders are filled
          queryClient.invalidateQueries({
            queryKey: ["trade-history", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["order-history", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["user-notifications", userId],
          });
          break;

        case "position_updated":
        case "position_liquidated":
          queryClient.invalidateQueries({
            queryKey: ["backend-positions", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["backend-account", userId],
          });
          break;

        case "deposit_credited":
          // Only show notification if it's for this user and not already shown (deduplication)
          if (event.user_id === userId && 
              prefs?.deposit_enabled &&
              !hasShownDepositNotification(event.tx_signature)) {
            // Mark as shown to prevent duplicates
            markDepositNotified(event.tx_signature);
            
            const title = "Deposit Received!";
            const message = `+${formatUsd(event.amount)} USDC has been credited to your account`;
            
            // Show toast IMMEDIATELY (don't wait for backend)
            if (prefs.deposit_toast) {
              toast.success(title, {
                description: (
                  <span>
                    <span className="text-bid-foreground font-semibold">
                      +{formatUsd(event.amount)} USDC
                    </span>{" "}
                    has been credited to your account
                  </span>
                ),
                duration: 8000,
                icon: <ArrowDownCircle className="text-bid-foreground" />,
              });
            }
            
            // Create notification in background (fire-and-forget, don't await)
            createNotification("deposit", "success", title, message, {
              amount: event.amount,
              tx_signature: event.tx_signature,
              new_balance: event.new_balance,
            });
          }
          // Refresh account data to show new balance
          queryClient.invalidateQueries({
            queryKey: ["backend-account", userId],
          });
          // Also refresh deposit history
          queryClient.invalidateQueries({
            queryKey: ["deposits", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["user-notifications", userId],
          });
          break;

        case "margin_warning":
          // Only show notification if it's for this user and cooldown has passed
          if (event.user_id === userId && prefs?.near_liquidation_enabled) {
            const warning = getWarningMessage(event.warning_level, event.market);
            if (warning && canShowMarginWarning(event.market, event.warning_level)) {
              // Record that we're showing this warning
              setLastWarningTime(event.market, event.warning_level);
              
              await createNotification("near_liquidation", "warning", warning.title, warning.description, {
                market: event.market,
                margin_ratio: event.margin_ratio,
                warning_level: event.warning_level,
              });
              
              const Icon = warning.icon;
              
              if (prefs.near_liquidation_toast) {
                if (event.warning_level === "danger") {
                  toast.error(warning.title, {
                    description: warning.description,
                    duration: 15000,
                    icon: <Icon className={warning.className} />,
                  });
                } else if (event.warning_level === "warning") {
                  toast.warning(warning.title, {
                    description: warning.description,
                    duration: 10000,
                    icon: <Icon className={warning.className} />,
                  });
                } else {
                  toast(warning.title, {
                    description: warning.description,
                    duration: 5000,
                    icon: <Icon className={warning.className} />,
                  });
                }
              } else {
                // Show as persistent notification
                addPersistentNotification({
                  severity: event.warning_level === "danger" ? "error" : "warning",
                  title: warning.title,
                  message: warning.description,
                  icon: "warning",
                });
              }
            }
          }
          // Refresh account and positions data
          queryClient.invalidateQueries({
            queryKey: ["backend-account", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["backend-positions", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["user-notifications", userId],
          });
          break;

        case "liquidation_executed":
          // Only show notification if it's for this user
          if (event.user_id === userId && prefs?.liquidation_enabled) {
            const pnl = parseFloat(event.pnl);
            const title = "Position Liquidated";
            const message = `Your ${event.market} position was liquidated. PnL: ${formatUsd(pnl)}`;
            
            // Save to notification history
            await createNotification("liquidation", "error", title, message, {
              market: event.market,
              pnl: event.pnl,
              penalty_fee: event.penalty_fee,
            });
            
            // Show toast or persistent based on preferences
            if (prefs.liquidation_toast) {
              toast.error(title, {
                description: (
                  <div className="space-y-1">
                    <p>Your {event.market} position has been liquidated.</p>
                    <p className="text-xs text-muted-foreground">
                      PnL: <span className={pnl >= 0 ? "text-bid-foreground" : "text-ask-foreground"}>
                        {formatUsd(pnl)}
                      </span>
                    </p>
                  </div>
                ),
                duration: 15000,
                icon: <Skull className="text-ask-foreground" />,
              });
            } else {
              addPersistentNotification({
                severity: "error",
                title,
                message,
                icon: "skull",
              });
            }
          }
          // Refresh all relevant data
          queryClient.invalidateQueries({
            queryKey: ["backend-positions", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["backend-account", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["liquidations", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["user-notifications", userId],
          });
          break;
          
        case "tp_sl_triggered":
          if (event.user_id === userId && prefs?.tp_sl_enabled) {
            const isProfit = event.trigger_type === "take_profit";
            const pnl = parseFloat(event.pnl);
            const title = isProfit ? "Take Profit Triggered" : "Stop Loss Triggered";
            const pnlText = pnl >= 0 ? `+${formatUsd(pnl)}` : formatUsd(pnl);
            const message = `Your ${event.market} ${isProfit ? "TP" : "SL"} was triggered at ${formatUsd(event.execution_price)}. PnL: ${pnlText}`;
            
            // Both TP and SL are positive events (protection worked!)
            await createNotification("tp_sl_triggered", "success", title, message, {
              market: event.market,
              trigger_type: event.trigger_type,
              execution_price: event.execution_price,
              pnl: event.pnl,
            });
            
            // Different icons: TrendingUp for TP, ShieldOff for SL (protection activated)
            const Icon = isProfit ? TrendingUp : ShieldOff;
            
            if (prefs.tp_sl_toast) {
              toast.success(title, {
                description: message,
                duration: 8000,
                icon: <Icon className="text-bid-foreground" />,
              });
            } else {
              // Persistent notifications only support warning/error, use warning for informational alerts
              addPersistentNotification({
                severity: "warning",
                title,
                message,
              });
            }
          }
          queryClient.invalidateQueries({
            queryKey: ["backend-positions", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["backend-account", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["user-notifications", userId],
          });
          break;
          
        case "withdrawal_status_changed":
          if (event.user_id === userId && prefs?.withdrawal_enabled) {
            const statusMessages: Record<string, { title: string; severity: string }> = {
              approved: { title: "Withdrawal Approved", severity: "success" },
              processing: { title: "Withdrawal Processing", severity: "info" },
              completed: { title: "Withdrawal Completed", severity: "success" },
              rejected: { title: "Withdrawal Rejected", severity: "error" },
              failed: { title: "Withdrawal Failed", severity: "error" },
            };
            
            const statusInfo = statusMessages[event.status] || { title: "Withdrawal Updated", severity: "info" };
            const message = event.rejection_reason 
              ? `${formatUsd(event.amount)} ${event.token} - Reason: ${event.rejection_reason}`
              : `${formatUsd(event.amount)} ${event.token}`;
            
            await createNotification("withdrawal", statusInfo.severity, statusInfo.title, message, {
              withdrawal_id: event.withdrawal_id,
              status: event.status,
              amount: event.amount,
            });
            
            if (prefs.withdrawal_toast) {
              const toastFn = statusInfo.severity === "error" ? toast.error : toast.success;
              toastFn(statusInfo.title, {
                description: message,
                duration: 8000,
                icon: <ArrowUpCircle />,
              });
            } else if (statusInfo.severity === "error") {
              addPersistentNotification({
                severity: "error",
                title: statusInfo.title,
                message,
              });
            }
          }
          queryClient.invalidateQueries({
            queryKey: ["withdrawals", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["user-notifications", userId],
          });
          break;
          
        case "account_status_changed":
          // Account security notifications are ALWAYS shown (critical security event)
          if (event.user_id === userId) {
            const title = `Account ${event.new_status.charAt(0).toUpperCase() + event.new_status.slice(1)}`;
            const message = event.reason || `Your account status has changed to ${event.new_status}`;
            
            await createNotification("account_security", "error", title, message, {
              old_status: event.old_status,
              new_status: event.new_status,
              reason: event.reason,
            });
            
            // Always show as persistent notification for critical security events
            // (account_security_toast defaults to false in DB, so we always use persistent)
            addPersistentNotification({
              severity: "error",
              title,
              message,
              icon: "shield",
            });
          }
          queryClient.invalidateQueries({
            queryKey: ["user-notifications", userId],
          });
          break;
          
        case "balance_adjusted":
          if (event.user_id === userId) {
            const title = "Balance Adjusted";
            const message = `${parseFloat(event.amount) >= 0 ? "+" : ""}${formatUsd(event.amount)} - ${event.reason}`;
            
            await createNotification("account_security", "info", title, message, {
              amount: event.amount,
              reason: event.reason,
              new_balance: event.new_balance,
            });
            
            toast.info(title, {
              description: message,
              duration: 8000,
            });
          }
          queryClient.invalidateQueries({
            queryKey: ["backend-account", userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["user-notifications", userId],
          });
          break;
      }
    },
    [queryClient, userId, addPersistentNotification]
  );

  // Use the new unified WebSocket client for events
  const { isConnected } = useWsEvents(handleEvent, !!userId);

  return {
    isConnected,
    lastEvent,
  };
};

